import { truncateText } from '@ardoq/graph-labeling';
import { ComponentLabelParts } from '../types';
import { HORIZONTAL_ELLIPSIS } from '@ardoq/global-consts';

// the visible string must be at least 3 characters long.
const MIN_VISIBLE_CHARACTERS = 3;
const OPTIMALLY_TRUNCATED_NUMBER_OF_CHARACTERS = 4;

type MeasureLabelPartsArgs = Pick<
  TruncateComponentLabelArgs,
  'measure' | 'label' | 'fieldValue'
> & {
  numberOfVisibleCharacters: number;
  ellipsisWidth: number;
};
const getTruncatedLabelPartsWidthForNumberOfChars = ({
  fieldValue,
  label,
  measure,
  numberOfVisibleCharacters,
  ellipsisWidth,
}: MeasureLabelPartsArgs) => ({
  minComponentNameWidth: label
    ? measure(label.slice(0, numberOfVisibleCharacters)) +
      (label.length > numberOfVisibleCharacters ? ellipsisWidth : 0)
    : 0,
  minFieldValueWidth: fieldValue
    ? measure(fieldValue.slice(0, numberOfVisibleCharacters)) +
      (fieldValue.length > numberOfVisibleCharacters ? ellipsisWidth : 0)
    : 0,
});

type GetFullSuffixPartsWidthArgs = Pick<
  TruncateComponentLabelArgs,
  'measure' | 'fieldValue' | 'fieldLabel'
>;
const getFullSuffixPartsWidth = ({
  measure,
  fieldLabel,
  fieldValue,
}: GetFullSuffixPartsWidthArgs) => {
  return {
    fieldValueWidth: fieldValue ? measure(fieldValue) : 0,
    fieldLabelWidth: fieldLabel ? measure(fieldLabel) : 0,
    colonAndSpaceWidth: fieldLabel ? measure(': ') : 0,
  };
};

export const getFullComponentLabelWithLegacyFormatting = ({
  label,
  fieldLabel,
  fieldValue,
}: ComponentLabelParts) => {
  const suffixText = fieldValue
    ? ` [${fieldLabel ? `${fieldLabel}: ` : ''}${fieldValue}]`
    : '';
  return `${label}${suffixText}`;
};

type TruncateComponentLabelArgs = ComponentLabelParts & {
  /** the available width. if you don't really need to truncate and just want the component label, you can pass Infinity here. */
  width: number;
  /** a function to measure strings. this should take into consideration the font in use. if you don't really need to truncate and just want the component label, you can pass a function that returns 0 here. */
  measure: (label: string) => number;
};
const truncateComponentLabel = ({
  fieldValue,
  fieldLabel,
  label,
  width,
  measure,
}: TruncateComponentLabelArgs) => {
  const truncate = truncateText(measure);
  const componentName = label ?? '';

  if (!fieldValue) {
    return truncate(componentName, width);
  }

  // #region - get initial measurments

  const ellipsisWidth = measure(HORIZONTAL_ELLIPSIS);

  if (!isFinite(width) || !ellipsisWidth) {
    return getFullComponentLabelWithLegacyFormatting({
      label: componentName,
      fieldLabel,
      fieldValue,
    });
  }

  const {
    minComponentNameWidth: optimalComponentNameMinWidth,
    minFieldValueWidth: optimalFieldValueMinWidth,
  } = getTruncatedLabelPartsWidthForNumberOfChars({
    fieldValue,
    label,
    measure,
    ellipsisWidth,
    numberOfVisibleCharacters: OPTIMALLY_TRUNCATED_NUMBER_OF_CHARACTERS,
  });

  const spaceAndBracketsWidth = measure(`${label ? ' ' : ''}[]`);

  // #endregion - get initial measurments

  // #region - determine the trunctaion limits

  // if there is enough space to display the optimally truncated stuff, do it.
  // if there is not enough space for the optimally truncated stuff, truncate stuff to the possible minimum.
  const isSufficientlyTruncated =
    spaceAndBracketsWidth +
      optimalComponentNameMinWidth +
      optimalFieldValueMinWidth <=
    width;

  const { minComponentNameWidth, minFieldValueWidth } = isSufficientlyTruncated
    ? {
        minComponentNameWidth: optimalComponentNameMinWidth,
        minFieldValueWidth: optimalFieldValueMinWidth,
      }
    : getTruncatedLabelPartsWidthForNumberOfChars({
        fieldValue,
        label,
        measure,
        ellipsisWidth,
        numberOfVisibleCharacters: MIN_VISIBLE_CHARACTERS,
      });

  // #endregion - determine the trunctaion limits

  // #region - determine what to truncate and how

  // If there is no space for "comp... [123...]", display "comp..."
  if (
    spaceAndBracketsWidth + minComponentNameWidth + minFieldValueWidth >
    width
  ) {
    return truncate(componentName, width);
  }

  const { fieldValueWidth, fieldLabelWidth, colonAndSpaceWidth } =
    getFullSuffixPartsWidth({ measure, fieldLabel, fieldValue });

  // If there is no field label, or not enough space for "Nam... [field: 123...]",
  // display the field value in the brackets, as in "Nam... [123...]".
  // LightweightSuffix is a suffix with only a filed value but no field label.
  const isLightweightSuffix =
    !fieldLabel ||
    minComponentNameWidth +
      spaceAndBracketsWidth +
      minFieldValueWidth +
      fieldLabelWidth +
      colonAndSpaceWidth >
      width;

  // The field value has priority. If it can be displayed in full, display it, truncate the component name.
  // If the component name is truncated to minComponentNameWidth and the field value has no space, truncate
  // the field value.
  // Field name should not be truncated, but the field value can be truncated to minFieldValueWidth.

  const fullSuffixWidthAdjustment = isLightweightSuffix
    ? 0
    : fieldLabelWidth + colonAndSpaceWidth;

  const untruncatedSuffixWidth =
    spaceAndBracketsWidth + fieldValueWidth + fullSuffixWidthAdjustment;

  const availableComponentNameWidth = Math.max(
    minComponentNameWidth,
    width - untruncatedSuffixWidth
  );

  const availableSuffixWidth = width - availableComponentNameWidth;

  const fieldValueTruncationWidth =
    availableSuffixWidth - spaceAndBracketsWidth - fullSuffixWidthAdjustment;

  // #endregion - determine what to truncate and how

  // #region - truncate and return

  const truncatedComponentName = truncate(
    componentName,
    availableComponentNameWidth
  );

  const truncatedFieldValue = truncate(fieldValue, fieldValueTruncationWidth);

  const suffixText = isLightweightSuffix
    ? `[${truncatedFieldValue}]`
    : `[${fieldLabel}: ${truncatedFieldValue}]`;

  return truncatedComponentName
    ? `${truncatedComponentName} ${suffixText}`
    : suffixText;
};
export { truncateComponentLabel };
